import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import styles from '../AgentActionGroups.styles';
import type { AgentAction as AgentActionType } from 'configuration-schemas/viewdefns/viewdefn';
import { AgentInput } from './AgentInput';
import { AgentPreview } from './AgentPreview';
import Paper from '@material-ui/core/Paper';
interface AgentActionProps {
  action: AgentActionType;
  actionIndex: number;
  groupText: string;
  isSelected: boolean;
  onActionClick: (actionIndex: number) => void;
  scope: {
    productMember: string | null;
    locationMember: string | null;
  };
}

interface StepSelections {
  [key: number]: string[];
}

interface StepCompletions {
  [key: number]: boolean;
}

export const AgentAction = ({ action, actionIndex, groupText, isSelected, onActionClick, scope }: AgentActionProps) => {
  const [stepSelections, setStepSelections] = useState<StepSelections>({});
  const [stepCompletions, setStepCompletions] = useState<StepCompletions>({});
  const navigate = useNavigate();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSelectionChanged = (stepIndex: number, selection: string[]) => {
    setStepSelections((prev) => ({
      ...prev,
      [stepIndex]: selection,
    }));
    scrollToBottom();
  };

  useEffect(() => {
    scrollToBottom();
  }, [stepSelections]);

  const handlePreviewDataLoaded = (stepIndex: number) => {
    setStepCompletions((prev) => ({
      ...prev,
      [stepIndex]: true,
    }));
    scrollToBottom();
  };

  const shouldRenderStep = (stepIndex: number): boolean => {
    // Always render the first step
    if (stepIndex === 0) return true;

    if (!action.steps) return false;

    const previousStep = action.steps[stepIndex - 1];

    // Check if previous step is completed
    if (previousStep.type === 'agentInput') {
      // For input steps, check if there's a selection
      return stepSelections[stepIndex - 1]?.length > 0;
    } else if (previousStep.type === 'agentPreview') {
      // For preview steps, check if data has loaded
      return stepCompletions[stepIndex - 1] === true;
    } else if (previousStep.type === 'link') {
      // Link steps are always considered complete
      return true;
    }

    return false;
  };

  return (
    <div key={`${groupText}-${action.text}-${actionIndex}`} className={styles.actionItem}>
      <div className={styles.actionHeader} onClick={() => onActionClick(actionIndex)}>
        <div className={styles.actionIconContainer}>
          <i className={action.icon} />
        </div>
        <div className={styles.actionTextContainer}>
          <div className={styles.actionDescription}>{action.description}</div>
          <div className={styles.actionTitle}>{action.text}</div>
        </div>
      </div>
      {isSelected && action.steps && (
        <div style={{ maxHeight: '100%', overflow: 'auto' }}>
          {action.steps.map((step, stepIndex) => {
            if (!shouldRenderStep(stepIndex)) return null;

            const stepContent = (() => {
              switch (step.type) {
                case 'agentInput':
                  return (
                    <AgentInput
                      step={step}
                      onSelectionChanged={(selection) => handleSelectionChanged(stepIndex, selection)}
                      onDataLoaded={scrollToBottom}
                    />
                  );
                case 'agentPreview':
                  return (
                    <AgentPreview step={step} scope={scope} onDataLoaded={() => handlePreviewDataLoaded(stepIndex)} />
                  );
                case 'link':
                  return (
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <button // TODO remove style tags and move to typestyle classes
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0.75rem',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s',
                          backgroundColor: '#a34ae3',
                          border: 'none',
                          borderRadius: '50%',
                          color: 'white',
                        }}
                        onClick={() => {
                          // This needs processDataApiParams like in EditableGrid breadcrumb links
                          if (step.params?.dataApi?.url) {
                            navigate(step.params.dataApi.url);
                          }
                        }}
                      >
                        <i className="fas fa-arrow-right" style={{ marginLeft: 'auto' }} />
                      </button>
                    </div>
                  );
                default:
                  return null;
              }
            })();

            // TODO add animated loading spinner
            return (
              <div key={stepIndex} className={styles.stepContainer}>
                {stepContent}
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>
      )}
    </div>
  );
};
